// ============================================================================
// Policy Templates & Compliance Configuration
// ============================================================================

import { v4 as uuidv4 } from 'uuid';

// ============================================================================
// COMPANY POLICY TEMPLATES (Generic - All States)
// ============================================================================

export const COMPANY_POLICIES = [
  {
    policyCode: 'EMPLOYEE_HANDBOOK',
    title: 'Employee Handbook Acknowledgment',
    category: 'company_policy',
    version: '2024.1',
    effectiveDate: '2024-01-01',
    requiresSignature: true,
    requiresAnnualReview: true,
    content: `
EMPLOYEE HANDBOOK ACKNOWLEDGMENT

I acknowledge that I have received access to the [Company Name] Employee Handbook (the "Handbook"). I understand that:

1. RECEIPT & REVIEW
I have received access to the current version of the Employee Handbook, and I agree to read and familiarize myself with its contents.

2. UNDERSTANDING
I understand that the Handbook contains important information about Company policies, procedures, and benefits, and I agree to comply with all policies contained therein.

3. AT-WILL EMPLOYMENT
I understand that my employment is "at-will" and that either the Company or I may terminate the employment relationship at any time, with or without cause or notice.

4. NOT A CONTRACT
I understand that the Handbook is not a contract of employment and does not create any express or implied contractual obligations.

5. CHANGES
I understand that the Company reserves the right to modify, amend, or terminate any policy, benefit, or procedure at any time, with or without notice.

6. SUPERSEDES PRIOR DOCUMENTS
I understand that this Handbook supersedes all previous handbooks, policies, and procedures.

7. QUESTIONS
I understand that I should consult with Human Resources if I have any questions about the policies in the Handbook.

By signing below, I acknowledge that I have received and reviewed the Employee Handbook.
    `,
  },
  {
    policyCode: 'CODE_OF_CONDUCT',
    title: 'Code of Conduct Acknowledgment',
    category: 'company_policy',
    version: '2024.1',
    effectiveDate: '2024-01-01',
    requiresSignature: true,
    requiresAnnualReview: true,
    content: `
CODE OF CONDUCT ACKNOWLEDGMENT

As an employee of [Company Name], I acknowledge and agree to the following Code of Conduct:

1. INTEGRITY
• I will conduct business with honesty and integrity
• I will not engage in fraud, theft, or dishonest conduct
• I will accurately report time, expenses, and business information

2. PROFESSIONALISM
• I will treat colleagues, customers, and vendors with respect
• I will maintain a professional demeanor and appearance
• I will communicate professionally in all business interactions

3. COMPLIANCE
• I will comply with all applicable laws, regulations, and company policies
• I will report any suspected violations to management or HR
• I will cooperate with any investigations

4. CONFIDENTIALITY
• I will protect confidential and proprietary information
• I will not share company information without authorization
• I will maintain the privacy of employee and customer data

5. CONFLICTS OF INTEREST
• I will disclose any potential conflicts of interest
• I will not engage in outside employment that conflicts with my duties
• I will not accept gifts or payments that could influence my judgment

6. WORKPLACE SAFETY
• I will follow all safety rules and procedures
• I will report unsafe conditions or accidents immediately
• I will not work under the influence of drugs or alcohol

7. USE OF COMPANY RESOURCES
• I will use company resources responsibly and for business purposes
• I will not use company property for personal gain
• I will protect company assets from loss or theft

8. REPORTING VIOLATIONS
I understand that I have a duty to report violations of this Code of Conduct and that the Company prohibits retaliation against anyone who reports in good faith.

By signing below, I acknowledge that I have read, understand, and agree to comply with this Code of Conduct.
    `,
  },
  {
    policyCode: 'IT_ACCEPTABLE_USE',
    title: 'IT & Acceptable Use Policy',
    category: 'company_policy',
    version: '2024.1',
    effectiveDate: '2024-01-01',
    requiresSignature: true,
    requiresAnnualReview: true,
    content: `
INFORMATION TECHNOLOGY & ACCEPTABLE USE POLICY

This policy governs the use of [Company Name]'s information technology resources.

1. COMPANY PROPERTY
All IT resources, including computers, phones, email, and network access, are Company property and are provided for business use.

2. NO EXPECTATION OF PRIVACY
You should have no expectation of privacy when using Company IT resources. The Company may monitor, access, and review any data stored on or transmitted through its systems.

3. ACCEPTABLE USE
Company IT resources should be used primarily for business purposes. Limited personal use is permitted if it:
• Does not interfere with work duties
• Does not violate any policy or law
• Does not consume excessive resources

4. PROHIBITED USE
You may NOT use Company IT resources to:
• Access, store, or transmit illegal, obscene, or offensive content
• Harass, threaten, or discriminate against anyone
• Download unauthorized software or files
• Share confidential information without authorization
• Engage in personal business or outside employment
• Circumvent security controls
• Send spam or mass unsolicited emails

5. PASSWORDS & SECURITY
• Keep passwords confidential and do not share them
• Use strong passwords and change them regularly
• Lock your workstation when away from your desk
• Report any security incidents immediately

6. EMAIL & COMMUNICATIONS
• Business emails are Company records and may be retained
• Do not send confidential information via unsecured email
• Be professional in all electronic communications

7. SOCIAL MEDIA
• Do not share confidential Company information on social media
• Do not represent yourself as speaking for the Company without authorization
• Be professional and respectful in online interactions

8. REMOTE ACCESS
• Use only approved methods to access Company systems remotely
• Ensure home networks and devices used for work are secure
• Do not use public Wi-Fi for confidential work without VPN

9. CONSEQUENCES
Violations of this policy may result in disciplinary action, up to and including termination, and may also result in civil or criminal liability.

By signing below, I acknowledge that I have read, understand, and agree to comply with this policy.
    `,
  },
  {
    policyCode: 'DRUG_FREE_WORKPLACE',
    title: 'Drug-Free Workplace Policy',
    category: 'company_policy',
    version: '2024.1',
    effectiveDate: '2024-01-01',
    requiresSignature: true,
    requiresAnnualReview: false,
    content: `
DRUG-FREE WORKPLACE POLICY

[Company Name] is committed to maintaining a drug-free workplace.

1. PROHIBITED CONDUCT
The following are prohibited on Company premises, in Company vehicles, or while conducting Company business:
• Possession, use, sale, or distribution of illegal drugs or controlled substances
• Being under the influence of alcohol or illegal drugs
• Misuse of prescription medications
• Possession of drug paraphernalia

2. PRESCRIPTION MEDICATIONS
• Employees taking prescription medications that may affect their ability to work safely must notify their supervisor
• Employees must follow their doctor's instructions for medication use
• Employees must not share prescription medications

3. ALCOHOL
• Alcohol consumption is prohibited during work hours
• Company-sponsored events where alcohol is served are exceptions, but responsible consumption is expected
• Employees must never drive Company vehicles while impaired

4. TESTING
The Company may conduct drug and alcohol testing:
• Pre-employment (where permitted by law)
• Reasonable suspicion
• Post-accident
• Random testing (for certain positions)
• Return-to-duty and follow-up

5. CONSEQUENCES
Violations of this policy may result in:
• Disciplinary action, up to and including termination
• Requirement to participate in a treatment program
• Legal consequences

6. EMPLOYEE ASSISTANCE
The Company encourages employees with substance abuse problems to seek help. Contact HR for information about available resources, including:
• Employee Assistance Program (EAP)
• Treatment referrals
• Leave for treatment

7. CONFIDENTIALITY
Information regarding drug and alcohol testing and treatment will be handled confidentially.

By signing below, I acknowledge that I have read, understand, and agree to comply with this policy.
    `,
  },
  {
    policyCode: 'WORKPLACE_VIOLENCE',
    title: 'Workplace Violence Prevention Policy',
    category: 'company_policy',
    version: '2024.1',
    effectiveDate: '2024-01-01',
    requiresSignature: true,
    requiresAnnualReview: true,
    content: `
WORKPLACE VIOLENCE PREVENTION POLICY

[Company Name] is committed to providing a safe work environment free from violence and threats.

1. ZERO TOLERANCE
The Company has zero tolerance for workplace violence, including:
• Physical assault or threats of assault
• Verbal abuse, intimidation, or bullying
• Harassment that creates fear of physical harm
• Possession of weapons on Company property
• Stalking
• Sabotage

2. PROHIBITED CONDUCT
The following conduct is prohibited:
• Threatening, intimidating, or hostile behavior
• Physical assault or fighting
• Brandishing a weapon
• Damaging property
• Making threatening statements or gestures
• Stalking or following co-workers

3. WEAPONS PROHIBITION
Weapons are prohibited on Company premises, including:
• Firearms
• Knives (other than small pocket knives)
• Explosives
• Any item used or intended to be used as a weapon

This prohibition applies to Company property, parking lots, and Company vehicles, regardless of whether the employee has a permit to carry.

4. REPORTING
All employees must immediately report:
• Any threats or acts of violence
• Suspicious behavior
• Weapons on Company property
• Domestic violence that may affect the workplace

Reports may be made to your supervisor, HR, or through the anonymous hotline.

5. INVESTIGATION
All reports will be investigated promptly. The Company will take appropriate action to address any threats and protect employees.

6. NO RETALIATION
Retaliation against anyone who reports workplace violence or participates in an investigation is strictly prohibited.

7. CONSEQUENCES
Violations of this policy may result in disciplinary action, up to and including termination, and may also result in criminal prosecution.

By signing below, I acknowledge that I have read, understand, and agree to comply with this policy.
    `,
  },
  {
    policyCode: 'SOCIAL_MEDIA_POLICY',
    title: 'Social Media Policy',
    category: 'company_policy',
    version: '2024.1',
    effectiveDate: '2024-01-01',
    requiresSignature: true,
    requiresAnnualReview: false,
    content: `
SOCIAL MEDIA POLICY

This policy governs employee use of social media as it relates to [Company Name].

1. DEFINITION
Social media includes all forms of online communication, including but not limited to:
• Social networks (Facebook, LinkedIn, Instagram, TikTok)
• Blogs and microblogs (Twitter/X)
• Video and photo sharing (YouTube, Snapchat)
• Online forums and message boards
• Review sites (Glassdoor, Yelp)

2. PERSONAL USE
During Work Hours:
• Social media for personal use should not interfere with work duties
• Excessive personal social media use during work hours is prohibited

Personal Accounts:
• If you identify yourself as a Company employee, state that your views are your own
• Do not share confidential Company information
• Do not make statements that could harm the Company's reputation

3. OFFICIAL COMPANY ACCOUNTS
Only authorized employees may post on behalf of the Company. If you are authorized:
• Follow brand guidelines and messaging
• Maintain professionalism
• Do not engage in controversial topics without approval
• Refer media inquiries to Communications/Marketing

4. GUIDELINES
DO:
• Be respectful and professional
• Protect confidential information
• Comply with all Company policies online
• Disclose your relationship with the Company when relevant
• Remember that posts may be permanent

DO NOT:
• Share confidential or proprietary information
• Make defamatory statements
• Harass or discriminate against anyone
• Speak on behalf of the Company without authorization
• Post content that violates any law or Company policy

5. PROTECTED ACTIVITY
This policy does not prohibit:
• Discussing wages, hours, or working conditions
• Engaging in concerted activity protected by law
• Filing complaints with government agencies

6. MONITORING
The Company may monitor public social media for mentions of the Company. Do not have an expectation of privacy for public posts.

By signing below, I acknowledge that I have read, understand, and agree to comply with this policy.
    `,
  },
  {
    policyCode: 'REMOTE_WORK',
    title: 'Remote Work Policy',
    category: 'company_policy',
    version: '2024.1',
    effectiveDate: '2024-01-01',
    requiresSignature: true,
    requiresAnnualReview: false,
    content: `
REMOTE WORK POLICY

This policy governs remote work arrangements at [Company Name].

1. ELIGIBILITY
Remote work may be available for eligible positions based on:
• Job duties and requirements
• Performance history
• Business needs
• Management approval

2. EXPECTATIONS
While working remotely, you are expected to:
• Maintain regular working hours and availability
• Remain accessible during core business hours
• Attend virtual meetings as scheduled
• Respond promptly to communications
• Meet productivity and performance standards

3. WORKSPACE REQUIREMENTS
Your remote workspace must:
• Be free from distractions
• Have reliable internet connection
• Be ergonomically appropriate
• Be secure and private for confidential work
• Meet safety standards

4. EQUIPMENT & EXPENSES
• Company equipment must be used for business purposes
• Protect Company equipment from damage and theft
• Expense reimbursement follows Company policy
• Internet and utility costs are generally employee responsibility

5. SECURITY
You must:
• Follow all IT security policies
• Use VPN for Company network access
• Keep devices locked when not in use
• Not allow others to use Company equipment
• Secure confidential documents and data

6. TIME TRACKING
Non-exempt employees must:
• Accurately record all hours worked
• Take required meal and rest periods
• Not work unauthorized overtime

7. COMMUNICATION
• Use Company-approved communication tools
• Check email and messages regularly
• Participate in team meetings and check-ins
• Communicate proactively about status and availability

8. TERMINATION OF ARRANGEMENT
Remote work arrangements may be modified or terminated:
• Due to performance issues
• Due to business needs
• At management discretion

By signing below, I acknowledge that I have read, understand, and agree to comply with this policy.
    `,
  },
  {
    policyCode: 'EXPENSE_REIMBURSEMENT',
    title: 'Expense Reimbursement Policy',
    category: 'company_policy',
    version: '2024.1',
    effectiveDate: '2024-01-01',
    requiresSignature: true,
    requiresAnnualReview: false,
    content: `
EXPENSE REIMBURSEMENT POLICY

This policy governs reimbursement of business expenses at [Company Name].

1. GENERAL PRINCIPLES
• Expenses must be reasonable, necessary, and business-related
• Prior approval is required for expenses over $[X]
• Original receipts are required for all expenses over $25
• Expenses must be submitted within 30 days

2. TRAVEL EXPENSES
Eligible travel expenses include:
• Airfare (economy class, with advance purchase when possible)
• Ground transportation (taxi, rideshare, rental car, mileage)
• Lodging (reasonable rate for the location)
• Meals (within per diem limits)
• Parking and tolls

3. MILEAGE
Personal vehicle use for business is reimbursed at the current IRS rate:
• Current Rate: $[0.67] per mile (2024)
• Commuting miles are not reimbursable

4. MEALS
• Individual meals: up to $[X] per meal
• Client entertainment: requires prior approval
• Alcohol: generally not reimbursable

5. NON-REIMBURSABLE EXPENSES
The following are NOT reimbursable:
• Personal expenses
• Traffic violations or parking tickets
• Personal entertainment
• Excessive or luxury items
• Expenses without receipts (over $25)

6. SUBMISSION PROCESS
• Submit expense reports via [expense system]
• Include itemized receipts
• Provide business purpose for each expense
• Obtain manager approval

7. CALIFORNIA REQUIREMENTS
Under California Labor Code Section 2802, the Company will reimburse employees for all necessary expenditures incurred in direct consequence of their job duties.

8. CONSEQUENCES
Fraudulent expense submissions may result in disciplinary action, up to and including termination.

By signing below, I acknowledge that I have read, understand, and agree to comply with this policy.
    `,
  },
];

// ============================================================================
// TRAINING COMPLIANCE REQUIREMENTS
// ============================================================================

export const TRAINING_REQUIREMENTS = {
  CA: [
    {
      trainingCode: 'CA_HARASSMENT_NONSUPERVISOR',
      title: 'Sexual Harassment Prevention Training (Non-Supervisory)',
      description: 'California law requires all employees to complete harassment prevention training within 6 months of hire and every 2 years thereafter.',
      legalReference: 'California Government Code Section 12950.1',
      duration: 60, // minutes
      requiredForRoles: ['employee'],
      frequency: 'biennial',
      dueWithinDays: 180, // within 6 months of hire
      renewalPeriod: 730, // every 2 years
      format: 'online',
      topics: [
        'Definition and examples of sexual harassment',
        'Federal and California laws prohibiting harassment',
        'Remedies available to victims',
        'Practical examples and quiz',
        'Abusive conduct (bullying)',
        'Resources for victims',
      ],
    },
    {
      trainingCode: 'CA_HARASSMENT_SUPERVISOR',
      title: 'Sexual Harassment Prevention Training (Supervisory)',
      description: 'California law requires supervisors to complete 2 hours of harassment prevention training within 6 months of assuming supervisory role and every 2 years thereafter.',
      legalReference: 'California Government Code Section 12950.1',
      duration: 120, // minutes
      requiredForRoles: ['supervisor', 'manager', 'admin', 'ceo'],
      frequency: 'biennial',
      dueWithinDays: 180,
      renewalPeriod: 730,
      format: 'online',
      topics: [
        'Definition and examples of sexual harassment',
        'Federal and California laws prohibiting harassment',
        'Supervisor responsibilities for prevention',
        'Steps to take when harassment is reported',
        'Remedies available to victims',
        'Abusive conduct (bullying)',
        'Practical examples and scenarios',
      ],
    },
    {
      trainingCode: 'CA_IIPP',
      title: 'Injury & Illness Prevention Program (IIPP) Training',
      description: 'All employees must be trained on workplace safety and the company IIPP.',
      legalReference: 'California Labor Code Section 6401.7',
      duration: 30,
      requiredForRoles: ['all'],
      frequency: 'upon_hire',
      dueWithinDays: 30,
      renewalPeriod: null, // varies by role/hazard
      format: 'online',
      topics: [
        'Company safety policies and procedures',
        'Hazard identification and reporting',
        'Emergency procedures',
        'Use of personal protective equipment',
        'Injury reporting procedures',
      ],
    },
  ],
  FEDERAL: [
    {
      trainingCode: 'SECURITY_AWARENESS',
      title: 'Cybersecurity Awareness Training',
      description: 'Training on protecting company data and systems.',
      legalReference: 'Industry Best Practice',
      duration: 45,
      requiredForRoles: ['all'],
      frequency: 'annual',
      dueWithinDays: 30,
      renewalPeriod: 365,
      format: 'online',
      topics: [
        'Password security',
        'Phishing and social engineering',
        'Data protection',
        'Safe internet practices',
        'Incident reporting',
      ],
    },
    {
      trainingCode: 'CODE_OF_CONDUCT',
      title: 'Code of Conduct Training',
      description: 'Training on company code of conduct and ethics.',
      legalReference: 'Company Policy',
      duration: 30,
      requiredForRoles: ['all'],
      frequency: 'annual',
      dueWithinDays: 30,
      renewalPeriod: 365,
      format: 'online',
      topics: [
        'Company values and ethics',
        'Conflicts of interest',
        'Confidentiality',
        'Reporting violations',
        'Consequences of violations',
      ],
    },
    {
      trainingCode: 'DEI_TRAINING',
      title: 'Diversity, Equity & Inclusion Training',
      description: 'Training on creating an inclusive workplace.',
      legalReference: 'Industry Best Practice',
      duration: 60,
      requiredForRoles: ['all'],
      frequency: 'annual',
      dueWithinDays: 90,
      renewalPeriod: 365,
      format: 'online',
      topics: [
        'Understanding unconscious bias',
        'Creating inclusive environments',
        'Respectful communication',
        'Allyship and advocacy',
        'Handling microaggressions',
      ],
    },
  ],
};

// ============================================================================
// COMPLIANCE CHECKLIST TEMPLATES
// ============================================================================

export const ONBOARDING_COMPLIANCE_CHECKLIST = {
  federal: [
    {
      itemCode: 'I9_SECTION1',
      title: 'Complete Form I-9 Section 1',
      description: 'Employee completes Section 1 of Form I-9',
      dueBy: 'first_day',
      requiredDocument: 'form_i9',
      category: 'employment_eligibility',
    },
    {
      itemCode: 'I9_SECTION2',
      title: 'Complete Form I-9 Section 2',
      description: 'Employer examines documents and completes Section 2',
      dueBy: 'third_day',
      requiredDocument: 'form_i9',
      category: 'employment_eligibility',
    },
    {
      itemCode: 'W4',
      title: 'Complete Form W-4',
      description: 'Employee completes federal tax withholding form',
      dueBy: 'first_day',
      requiredDocument: 'form_w4',
      category: 'tax_forms',
    },
    {
      itemCode: 'DIRECT_DEPOSIT',
      title: 'Complete Direct Deposit Form',
      description: 'Set up direct deposit for payroll',
      dueBy: 'first_week',
      requiredDocument: 'direct_deposit',
      category: 'payroll',
    },
    {
      itemCode: 'EMERGENCY_CONTACT',
      title: 'Provide Emergency Contact Information',
      description: 'Submit emergency contact details',
      dueBy: 'first_day',
      requiredDocument: 'emergency_contact',
      category: 'personal_info',
    },
  ],
  california: [
    {
      itemCode: 'DE4',
      title: 'Complete Form DE-4',
      description: 'Employee completes California state tax withholding form',
      dueBy: 'first_day',
      requiredDocument: 'form_de4',
      category: 'tax_forms',
    },
    {
      itemCode: 'WTPN',
      title: 'Acknowledge Wage Theft Prevention Notice',
      description: 'Review and acknowledge WTPN',
      dueBy: 'first_day',
      requiredDocument: 'wtpn',
      category: 'notices',
    },
    {
      itemCode: 'SICK_LEAVE_NOTICE',
      title: 'Acknowledge Paid Sick Leave Notice',
      description: 'Review and acknowledge paid sick leave rights',
      dueBy: 'first_day',
      requiredDocument: 'sick_leave_notice',
      category: 'notices',
    },
    {
      itemCode: 'WORKERS_COMP_NOTICE',
      title: "Acknowledge Workers' Compensation Notice",
      description: "Review and acknowledge workers' compensation rights",
      dueBy: 'first_day',
      requiredDocument: 'workers_comp_notice',
      category: 'notices',
    },
    {
      itemCode: 'MIN_WAGE_NOTICE',
      title: 'Acknowledge Minimum Wage Notice',
      description: 'Review and acknowledge minimum wage information',
      dueBy: 'first_day',
      requiredDocument: 'min_wage_notice',
      category: 'notices',
    },
    {
      itemCode: 'DV_LEAVE_NOTICE',
      title: 'Acknowledge DV/SA/Stalking Leave Notice',
      description: 'Review and acknowledge leave rights for DV victims',
      dueBy: 'first_day',
      requiredDocument: 'dv_leave_notice',
      category: 'notices',
    },
    {
      itemCode: 'UI_NOTICE',
      title: 'Acknowledge Unemployment Insurance Notice',
      description: 'Review and acknowledge UI information',
      dueBy: 'first_day',
      requiredDocument: 'ui_notice',
      category: 'notices',
    },
    {
      itemCode: 'SDI_NOTICE',
      title: 'Acknowledge State Disability Insurance Notice',
      description: 'Review and acknowledge SDI information',
      dueBy: 'first_day',
      requiredDocument: 'sdi_notice',
      category: 'notices',
    },
    {
      itemCode: 'PFL_NOTICE',
      title: 'Acknowledge Paid Family Leave Notice',
      description: 'Review and acknowledge PFL information',
      dueBy: 'first_day',
      requiredDocument: 'pfl_notice',
      category: 'notices',
    },
  ],
  policies: [
    {
      itemCode: 'HANDBOOK',
      title: 'Acknowledge Employee Handbook',
      description: 'Read and acknowledge receipt of employee handbook',
      dueBy: 'first_week',
      requiredDocument: 'handbook_ack',
      category: 'policies',
    },
    {
      itemCode: 'CODE_OF_CONDUCT',
      title: 'Acknowledge Code of Conduct',
      description: 'Read and acknowledge code of conduct',
      dueBy: 'first_week',
      requiredDocument: 'code_of_conduct_ack',
      category: 'policies',
    },
    {
      itemCode: 'AT_WILL',
      title: 'Acknowledge At-Will Employment',
      description: 'Sign at-will employment acknowledgment',
      dueBy: 'first_day',
      requiredDocument: 'at_will_ack',
      category: 'policies',
    },
    {
      itemCode: 'ANTI_HARASSMENT',
      title: 'Acknowledge Anti-Harassment Policy',
      description: 'Read and acknowledge anti-harassment policy',
      dueBy: 'first_week',
      requiredDocument: 'anti_harassment_ack',
      category: 'policies',
    },
    {
      itemCode: 'IT_POLICY',
      title: 'Acknowledge IT Acceptable Use Policy',
      description: 'Read and acknowledge IT policy',
      dueBy: 'first_week',
      requiredDocument: 'it_policy_ack',
      category: 'policies',
    },
    {
      itemCode: 'CONFIDENTIALITY',
      title: 'Sign Confidentiality Agreement',
      description: 'Sign confidentiality/NDA agreement',
      dueBy: 'first_day',
      requiredDocument: 'confidentiality_ack',
      category: 'agreements',
    },
    {
      itemCode: 'CCPA_PRIVACY',
      title: 'Acknowledge CCPA Privacy Notice',
      description: 'Acknowledge receipt of CCPA employee privacy notice',
      dueBy: 'first_day',
      requiredDocument: 'ccpa_privacy_ack',
      category: 'agreements',
    },
  ],
  training: [
    {
      itemCode: 'HARASSMENT_TRAINING',
      title: 'Complete Harassment Prevention Training',
      description: 'Complete required sexual harassment prevention training',
      dueBy: '180_days',
      requiredDocument: 'harassment_training_cert',
      category: 'training',
    },
    {
      itemCode: 'SAFETY_TRAINING',
      title: 'Complete Safety Training (IIPP)',
      description: 'Complete injury and illness prevention training',
      dueBy: '30_days',
      requiredDocument: 'safety_training_cert',
      category: 'training',
    },
    {
      itemCode: 'SECURITY_TRAINING',
      title: 'Complete Cybersecurity Training',
      description: 'Complete cybersecurity awareness training',
      dueBy: '30_days',
      requiredDocument: 'security_training_cert',
      category: 'training',
    },
  ],
};

// ============================================================================
// COMPLIANCE STATUS DEFINITIONS
// ============================================================================

export const COMPLIANCE_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  OVERDUE: 'overdue',
  EXPIRED: 'expired',
  PENDING_REVIEW: 'pending_review',
  REJECTED: 'rejected',
};

export const COMPLIANCE_PRIORITY = {
  CRITICAL: 'critical', // Must be done on first day
  HIGH: 'high', // Must be done within 3 days
  MEDIUM: 'medium', // Must be done within first week
  LOW: 'low', // Must be done within 30 days
  ONGOING: 'ongoing', // Annual/recurring requirement
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all policies for a specific category
 */
export function getPoliciesByCategory(category) {
  return COMPANY_POLICIES.filter((p) => p.category === category);
}

/**
 * Get all training requirements for a role and state
 */
export function getTrainingRequirements(role, stateCode = 'CA') {
  const stateTraining = TRAINING_REQUIREMENTS[stateCode] || [];
  const federalTraining = TRAINING_REQUIREMENTS.FEDERAL || [];

  const allTraining = [...stateTraining, ...federalTraining];

  return allTraining.filter(
    (t) => t.requiredForRoles.includes('all') || t.requiredForRoles.includes(role)
  );
}

/**
 * Get onboarding checklist for a state
 */
export function getOnboardingChecklist(stateCode = 'CA') {
  const checklist = {
    federal: ONBOARDING_COMPLIANCE_CHECKLIST.federal,
    state: [],
    policies: ONBOARDING_COMPLIANCE_CHECKLIST.policies,
    training: ONBOARDING_COMPLIANCE_CHECKLIST.training,
  };

  if (stateCode === 'CA') {
    checklist.state = ONBOARDING_COMPLIANCE_CHECKLIST.california;
  }

  return checklist;
}

/**
 * Calculate due date based on hire date and due by rule
 */
export function calculateDueDate(hireDate, dueBy) {
  const date = new Date(hireDate);

  switch (dueBy) {
    case 'first_day':
      return date;
    case 'third_day':
      date.setDate(date.getDate() + 3);
      return date;
    case 'first_week':
      date.setDate(date.getDate() + 7);
      return date;
    case '30_days':
      date.setDate(date.getDate() + 30);
      return date;
    case '90_days':
      date.setDate(date.getDate() + 90);
      return date;
    case '180_days':
      date.setDate(date.getDate() + 180);
      return date;
    default:
      if (dueBy && dueBy.endsWith('_days')) {
        const days = parseInt(dueBy.replace('_days', ''), 10);
        date.setDate(date.getDate() + days);
        return date;
      }
      return date;
  }
}

/**
 * Determine compliance priority based on due date and current date
 */
export function getCompliancePriority(item, hireDate) {
  const dueDate = calculateDueDate(hireDate, item.dueBy);
  const today = new Date();
  const daysUntilDue = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24));

  if (daysUntilDue < 0) {
    return COMPLIANCE_PRIORITY.CRITICAL;
  } else if (daysUntilDue <= 1) {
    return COMPLIANCE_PRIORITY.CRITICAL;
  } else if (daysUntilDue <= 3) {
    return COMPLIANCE_PRIORITY.HIGH;
  } else if (daysUntilDue <= 7) {
    return COMPLIANCE_PRIORITY.MEDIUM;
  } else {
    return COMPLIANCE_PRIORITY.LOW;
  }
}

/**
 * Check if a training is due for renewal
 */
export function isTrainingDueForRenewal(completionDate, renewalPeriod) {
  if (!renewalPeriod) return false;

  const completion = new Date(completionDate);
  const renewalDate = new Date(completion);
  renewalDate.setDate(renewalDate.getDate() + renewalPeriod);

  return new Date() >= renewalDate;
}

/**
 * Generate compliance report data
 */
export function generateComplianceReportData(employees, submissions, stateCode = 'CA') {
  const checklist = getOnboardingChecklist(stateCode);
  const allItems = [
    ...checklist.federal,
    ...checklist.state,
    ...checklist.policies,
    ...checklist.training,
  ];

  const report = {
    totalEmployees: employees.length,
    totalItems: allItems.length,
    complianceRate: 0,
    byCategory: {},
    overdue: [],
    upcoming: [],
  };

  // Calculate compliance for each employee
  let totalCompleted = 0;
  let totalRequired = 0;

  employees.forEach((emp) => {
    allItems.forEach((item) => {
      totalRequired++;
      const submission = submissions.find(
        (s) => s.userId === emp.id && s.itemCode === item.itemCode
      );
      if (submission && submission.status === COMPLIANCE_STATUS.COMPLETED) {
        totalCompleted++;
      }
    });
  });

  report.complianceRate = totalRequired > 0 ? Math.round((totalCompleted / totalRequired) * 100) : 100;

  return report;
}

export default {
  COMPANY_POLICIES,
  TRAINING_REQUIREMENTS,
  ONBOARDING_COMPLIANCE_CHECKLIST,
  COMPLIANCE_STATUS,
  COMPLIANCE_PRIORITY,
  getPoliciesByCategory,
  getTrainingRequirements,
  getOnboardingChecklist,
  calculateDueDate,
  getCompliancePriority,
  isTrainingDueForRenewal,
  generateComplianceReportData,
};
